import { matchesUserSearch } from './userSearch'
import React, { useMemo, useCallback, useState, memo } from 'react'
import { Box, Flex } from '@chakra-ui/core'
import { SelectionList } from '../SelectionList'
import useSelection, { SelectionMode } from '../OptimizedGroupsTree/hooks/useSelection'
import { RootState, IConfigRetailUser } from 'constants/interfaces'
import { useSelector } from 'react-redux'
import { UserRow } from './UserRow'
import { NoResults } from '../GroupsTree/NoResults'
import { SearchInput } from 'components/ui/Input/SearchInput'
import { Button } from 'components/ui/Button'
import { isRtlSelector } from 'redux/selectors/general'
import colors from 'constants/colors'
import useLocaleText from 'components/useLocaleText'
const getId = (user: IConfigRetailUser) => user.uid
const EMPTY_SELECTED_IDS: string[] = []

interface UsersListProps {
  selectionSections?: { selected: string; unselected: string }
  showSearch?: boolean
  searchWidth?: string
  searchActions?: React.ReactNode
  selectionMode?: SelectionMode
  initialSelectedIds?: string[]
  lockedSelectedIds?: string[]
  listHeight?: number
  onSelectionChange?: (selectedIds: string[]) => void
  isShowTags?: boolean
  // Restrict the list to these uids (e.g. the caller's org-tree scope). Undefined = show everyone.
  allowedUserIds?: string[]
}

const UsersList: React.FC<UsersListProps> = memo(
  ({
    selectionSections,
    showSearch = true,
    searchWidth = '100%',
    searchActions,
    selectionMode = SelectionMode.MULTIPLE,
    initialSelectedIds = EMPTY_SELECTED_IDS,
    lockedSelectedIds = EMPTY_SELECTED_IDS,
    listHeight = 600,
    onSelectionChange,
    isShowTags = false,
    allowedUserIds,
  }) => {
    const isRtl = useSelector(isRtlSelector)
    const { retail_users: allUsers, mappedGroupsByUsers } = useSelector((state: RootState) => state.config.config)
    const [searchTerm, setSearchTerm] = useState('')
    const allowedUserIdsSet = useMemo(() => (allowedUserIds ? new Set(allowedUserIds) : null), [allowedUserIds])
    const users = useMemo(
      () => (allowedUserIdsSet ? (allUsers || []).filter((user) => allowedUserIdsSet.has(user.uid)) : allUsers || []),
      [allUsers, allowedUserIdsSet]
    )
    const t_select_all = useLocaleText('select_all_stores_button')
    const t_clear = useLocaleText('group_unselecte_filter')
    const searchLabel = useLocaleText('search')
    const lockedSelectedIdsSet = useMemo(() => new Set(lockedSelectedIds), [lockedSelectedIds])
    const selectedIdsWithLocked = useMemo(
      () =>
        lockedSelectedIds.length > 0
          ? Array.from(new Set([...initialSelectedIds, ...lockedSelectedIds]))
          : initialSelectedIds,
      [initialSelectedIds, lockedSelectedIds]
    )

    const handleSelectionChange = useCallback(
      (selectedIds: string[]) => {
        onSelectionChange?.(Array.from(new Set([...selectedIds, ...lockedSelectedIds])))
      },
      [lockedSelectedIds, onSelectionChange]
    )

    const selectionOptions = useMemo(
      () => ({
        mode: selectionMode,
        initialSelectedIds: selectedIdsWithLocked,
        allIds: users.map((user) => user.uid),
        onSelectionChange: handleSelectionChange,
      }),
      [selectionMode, selectedIdsWithLocked, users, handleSelectionChange]
    )

    const { selectedIds, toggleSelection, isSelected, selectAll, resetSelection, isAllSelected } =
      useSelection(selectionOptions)

    const filteredUsers = useMemo(() => {
      if (!searchTerm.trim()) return users || []

      return (users || []).filter((user) => {
        const userGroup = mappedGroupsByUsers?.[user.uid]?.[0] ?? ''

        return matchesUserSearch(user, userGroup, searchTerm)
      })
    }, [users, searchTerm, mappedGroupsByUsers])

    const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value)
    }, [])

    const toggleSelectAll = useCallback(() => {
      if (isAllSelected) {
        resetSelection(lockedSelectedIds)
      } else {
        selectAll()
      }
    }, [isAllSelected, lockedSelectedIds, resetSelection, selectAll])

    const renderItem = useCallback(
      (user: IConfigRetailUser, style: React.CSSProperties) => {
        return (
          <UserRow
            user={user}
            isSelected={isSelected(user.uid)}
            toggleSelection={toggleSelection}
            style={style}
            key={user.uid}
            isShowTags={isShowTags}
            selectionMode={selectionMode}
            isLocked={lockedSelectedIdsSet.has(user.uid)}
          />
        )
      },
      [isSelected, isShowTags, lockedSelectedIdsSet, selectionMode, toggleSelection]
    )

    return (
      <Box>
        {showSearch && (
          <Flex w="100%" flexDir="row" alignItems="center" justifyContent="center" p={2} style={{ gap: 8 }}>
            <Box width={searchWidth} minWidth="0">
              <SearchInput
                onChange={handleSearch}
                value={searchTerm}
                onClear={() => setSearchTerm('')}
                placeholder={searchLabel}
                aria-label={searchLabel}
                direction={isRtl ? 'rtl' : 'ltr'}
              />
            </Box>
            {selectionMode === SelectionMode.MULTIPLE && !searchTerm && (
              <Button
                variant="ghost"
                type="button"
                h="36px"
                w="max-content"
                minWidth="100px"
                onClick={searchActions ? selectAll : toggleSelectAll}
                borderRadius="5px"
                fontWeight="normal"
                borderColor={colors.greyLight}
              >
                {isAllSelected && !searchActions ? t_clear : t_select_all}
              </Button>
            )}
            {searchActions}
          </Flex>
        )}

        <Box h={listHeight}>
          {filteredUsers.length > 0 ? (
            <SelectionList
              items={filteredUsers}
              getId={getId}
              selectedIds={selectedIds}
              sections={selectionSections}
              direction={isRtl ? 'rtl' : 'ltr'}
              height={listHeight}
              renderItem={renderItem}
            />
          ) : (
            <NoResults />
          )}
        </Box>
      </Box>
    )
  }
)

export default UsersList
